import { useMemo } from "react";

import Typography from "ds/components/Typography";
import Box from "ds/components/Box";
import Counter from "ds/components/Counter";
import { SpacesContext } from "views/Account/SpacesProvider";
import useTypedContext from "hooks/useTypedContext";

import AssignRoleFormListItem from "./ListItem";
import AssignRoleFormFormAdd from "./Add";
import { AssignRoleFormEditFields, AssignRoleFormAddFields } from "./types";

type AssignRoleFormProps = {
  onCreate: (formData: AssignRoleFormAddFields) => void;
  onEdit: (formData: AssignRoleFormEditFields, roleAssignmentId: string) => void;
  onDelete: (roleAssignmentId: string, roleName: string) => void;
  setDirtyFields: (formId: string, value: boolean) => void;
  availableRoles: Array<{ id: string; name: string }>;
  assignedRoles: Array<{ id: string; roleID: string; spaceID: string }>;
  loading: boolean;
  createLoading?: boolean;
  selectableRoleIds?: Array<string>;
  selectableSpaceIds?: Array<string>;
  canEdit?: boolean;
};

const AssignRoleForm = ({
  onCreate,
  onEdit,
  onDelete,
  setDirtyFields,
  availableRoles,
  assignedRoles,
  loading,
  createLoading,
  selectableRoleIds,
  selectableSpaceIds,
  canEdit = true,
}: AssignRoleFormProps) => {
  const { manageableSpaces } = useTypedContext(SpacesContext);

  const assignedRolesCount = assignedRoles.length;

  const allRolesById = useMemo(
    () =>
      availableRoles?.reduce(
        (prev, next) => ({ ...prev, [next.id]: next.name }),
        {} as Record<string, string>
      ) || {},
    [availableRoles]
  );

  const spaceNamesById = useMemo(
    () =>
      manageableSpaces?.reduce(
        (prev, next) => ({ ...prev, [next.id]: next.name }),
        {} as Record<string, string>
      ) || {},
    [manageableSpaces]
  );

  const selectableRolesForAdd = useMemo(() => {
    return selectableRoleIds
      ? availableRoles.filter((role) => selectableRoleIds.includes(role.id))
      : availableRoles;
  }, [availableRoles, selectableRoleIds]);

  const selectableSpacesForAdd = useMemo(() => {
    return selectableSpaceIds
      ? manageableSpaces.filter((space) => selectableSpaceIds.includes(space.id))
      : manageableSpaces;
  }, [manageableSpaces, selectableSpaceIds]);

  return (
    <>
      <Box direction="column" gap="large">
        <AssignRoleFormFormAdd
          createLoading={createLoading}
          isLoading={loading}
          setIsDirty={setDirtyFields}
          selectableRoles={selectableRolesForAdd}
          selectableSpaces={selectableSpacesForAdd}
          onCreate={onCreate}
        />

        {assignedRolesCount > 0 && (
          <Box direction="column">
            <Box gap="small">
              <Typography tag="h5" variant="p-t5">
                Roles assigned
              </Typography>

              <Counter count={assignedRolesCount} size="small" />
            </Box>
          </Box>
        )}

        {assignedRoles.map((roleAssignment) => (
          <AssignRoleFormListItem
            setIsDirty={setDirtyFields}
            roleAssignmentId={roleAssignment.id}
            key={roleAssignment.id}
            roleId={roleAssignment.roleID}
            roleName={allRolesById[roleAssignment.roleID]}
            spaceId={roleAssignment.spaceID}
            spaceName={spaceNamesById[roleAssignment.spaceID] || ""}
            availableRoles={availableRoles}
            selectableRoleIds={selectableRoleIds}
            selectableSpaceIds={selectableSpaceIds}
            canEdit={canEdit}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        ))}
      </Box>
    </>
  );
};

export default AssignRoleForm;
